<div class="users-page">
  <div class="users-header">
    <div class="header-left">
      <h1>Users</h1>
      <p>
        Showing page {{ pageNumber }} of {{ totalPages || 1 }} 
        (Total {{ totalCount }} users)
      </p>
    </div>

    <div class="header-right">
      <div class="search-box">
        <span class="search-icon">⌕</span>
        <input 
          type="text" 
          [value]="searchTerm" 
          (input)="onSearchChange($event)" 
          placeholder="Search users..." 
        />
      </div>

      <button type="button" class="create-btn" (click)="onCreateUser()">
        Create
      </button>
    </div>
  </div>

  @if (isLoading) {
    <div class="empty-state">Loading users...</div>
  } @else {
    <div class="users-table-card">
      <div class="table-scroll">
        <table class="users-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>ROLE</th>
              <th>FIRST NAME</th>
              <th>LAST NAME</th>
              <th>PHONE NUMBER</th>
              <th>EMAIL (USERNAME)</th>
              <th>ACTIVE</th>
              <th>ACTIONS</th>
            </tr>
          </thead>

          <tbody>
            @for (user of users; track user.userId) {
              <tr>
                <td>{{ user.userId }}</td>
                <td>{{ user.roleName }}</td>
                <td>{{ user.firstName }}</td>
                <td>{{ user.lastName }}</td>
                <td>{{ user.phoneNumber }}</td>
                <td class="truncate-cell">
                  {{ user.email }}<br>
                  <small class="text-muted">{{ user.username }}</small>
                </td>
                <td>
                  <span class="status-badge" [class.active]="user.isActive">
                    {{ user.isActive ? 'Yes' : 'No' }}
                  </span>
                </td>
                <td>
                  <div class="action-buttons">
                    <button
                      type="button"
                      class="icon-action edit"
                      (click)="openEditUserModal(user)"
                      [disabled]="user.roleId === 1 && !isSuperAdmin()"
                      [title]="user.roleId === 1 && !isSuperAdmin() ? 'Only Super Admin can edit Super Admin' : 'Edit'"
                    >
                      <img src="/icons/edit.svg" alt="Edit" class="action-icon" />
                    </button>

                    <button
                      type="button"
                      class="icon-action reset"
                      (click)="onResetPassword(user)"
                      [disabled]="(resettingUserId === user.userId) || (user.roleId === 1 && !isSuperAdmin())"
                      [title]="user.roleId === 1 && !isSuperAdmin() ? 'Only Super Admin can reset Super Admin password' : 'Reset Password'"
                    >
                      <img src="/icons/reset.svg" alt="Reset Password" class="action-icon" />
                    </button>

                    <button
                      type="button"
                      class="icon-action delete"
                      (click)="onDeleteUser(user)"
                      [disabled]="(deletingUserId === user.userId) || (user.roleId === 1 && !isSuperAdmin())"
                      [title]="user.roleId === 1 && !isSuperAdmin() ? 'Only Super Admin can delete Super Admin' : 'Delete'"
                    >
                      <img src="/icons/delete.svg" alt="Delete" class="action-icon" />
                    </button>
                  </div>
                </td>
              </tr>
            }
            @if (users.length === 0) {
              <tr>
                <td colspan="8" class="text-center py-4">No users found.</td>
              </tr>
            }
          </tbody>
        </table>
      </div>
      
      <!-- Pagination Controls -->
      <div class="pagination-controls">
        <button 
          type="button" 
          class="btn-pagination" 
          (click)="previousPage()" 
          [disabled]="pageNumber === 1 || isLoading">
          Previous
        </button>
        <span class="page-info">Page {{ pageNumber }} of {{ totalPages || 1 }}</span>
        <button 
          type="button" 
          class="btn-pagination" 
          (click)="nextPage()" 
          [disabled]="pageNumber >= totalPages || isLoading">
          Next
        </button>
      </div>
    </div>
  }

  @if (isCreateModalOpen) {
    <div class="modal-overlay" (click)="closeCreateUserModal()">
      <div class="user-modal" (click)="$event.stopPropagation()">
        <div class="modal-header">
          <div>
            <h2>{{ isEditMode ? 'Edit User' : 'Create New User' }}</h2>
            <p>
              {{
                isEditMode
                  ? 'Update user information.'
                  : 'Add a new user to the system. Fill in all required fields.'
              }}
            </p>
          </div>
          <button type="button" class="close-btn" (click)="closeCreateUserModal()">×</button>
        </div>

        <form [formGroup]="userForm" (ngSubmit)="saveUser()" class="modal-form">
          <div class="form-grid two-col">
            <div class="form-field">
              <label>First Name *</label>
              <input type="text" formControlName="firstName" />
              @if (firstNameControl?.touched && firstNameControl?.errors?.['required']) {
                <div class="field-error">First name is required.</div>
              }
            </div>

            <div class="form-field">
              <label>Last Name *</label>
              <input type="text" formControlName="lastName" />
              @if (lastNameControl?.touched && lastNameControl?.errors?.['required']) {
                <div class="field-error">Last name is required.</div>
              }
            </div>
          </div>

          <div class="form-grid two-col">
            <div class="form-field">
              <label>Email *</label>
              <input type="email" formControlName="email" />
              @if (emailControl?.touched && emailControl?.errors?.['required']) {
                <div class="field-error">Email is required.</div>
              }
              @if (emailControl?.touched && emailControl?.errors?.['email']) {
                <div class="field-error">Enter a valid email address.</div>
              }
            </div>

            <div class="form-field">
              <label>Username *</label>
              <input type="text" formControlName="username" />
              @if (usernameControl?.touched && usernameControl?.errors?.['required']) {
                <div class="field-error">Username is required.</div>
              }
            </div>
          </div>

          <div class="form-grid one-col">
            <div class="form-field">
              <label>Phone Number *</label>
              <input type="text" formControlName="phoneNumber" />
              @if (phoneNumberControl?.touched && phoneNumberControl?.errors?.['required']) {
                <div class="field-error">Phone number is required.</div>
              }
            </div>
          </div>

          <div class="form-grid two-col">
            <div class="form-field">
              <label>Role</label>
              <select formControlName="roleId">
                @for (role of roleOptions(); track role.id) {
                  <option [ngValue]="role.id">{{ role.name }}</option>
                }
              </select>
            </div>

            <div class="form-field">
              <label>Status</label>
              <select formControlName="status">
                @for (status of statusOptions; track status) {
                  <option [value]="status">{{ status }}</option>
                }
              </select>
            </div>
          </div>

          <div class="modal-actions">
            <button type="button" class="btn-cancel" (click)="closeCreateUserModal()">
              Cancel
            </button>
            <button type="submit" class="btn-save" [disabled]="isSaving">
              {{
                isSaving
                  ? isEditMode
                    ? 'Updating...'
                    : 'Creating...'
                  : isEditMode
                    ? 'Update User'
                    : 'Create User'
              }}
            </button>
          </div>
        </form>
      </div>
    </div>
  }
</div>

@if (isDeleteModalOpen()) {
  <div class="modal-overlay" (click)="closeDeleteModal()">
    <div class="delete-modal" (click)="$event.stopPropagation()">
      <div class="delete-modal-header">
        <h3>Delete User</h3>
        <button type="button" class="modal-close-btn" (click)="closeDeleteModal()">×</button>
      </div>

      <div class="delete-modal-body">
        <p>
          Are you sure you want to delete
          <strong>{{ deletingUser?.firstName }} {{ deletingUser?.lastName }}</strong>?
        </p>
      </div>

      <div class="delete-modal-actions">
        <button type="button" class="delete-cancel-btn" (click)="closeDeleteModal()">Cancel</button>
        <button
          type="button"
          class="delete-confirm-btn"
          (click)="confirmDeleteUser()"
          [disabled]="deletingUserId !== null"
        >
          {{ deletingUserId !== null ? 'Deleting...' : 'Delete' }}
        </button>
      </div>
    </div>
  </div>
}

@if (isResetModalOpen()) {
  <div class="modal-overlay" (click)="closeResetModal()">
    <div class="delete-modal" (click)="$event.stopPropagation()">
      <div class="delete-modal-header">
        <h3>Reset Password</h3>
        <button type="button" class="modal-close-btn" (click)="closeResetModal()">×</button>
      </div>

      <div class="delete-modal-body">
        <p>
          Are you sure you want to reset the password for
          <strong>{{ resettingUser?.firstName }} {{ resettingUser?.lastName }}</strong>?
        </p>
      </div>

      <div class="delete-modal-actions">
        <button type="button" class="delete-cancel-btn" (click)="closeResetModal()">Cancel</button>
        <button
          type="button"
          class="btn-save"
          style="padding: 0 1.25rem; font-weight: 600;"
          (click)="confirmResetPassword()"
          [disabled]="resettingUserId !== null"
        >
          {{ resettingUserId !== null ? 'Resetting...' : 'OK' }}
        </button>
      </div>
    </div>
  </div>
}

@if (errorMessage) {
  <div class="global-error-overlay" (click)="clearError()">
    <div class="error-toast" (click)="$event.stopPropagation()">
      <div class="error-icon">
        <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
      </div>
      <div class="error-content">
        <h3>Something went wrong</h3>
        <p>{{ errorMessage }}</p>
        <div class="error-actions">
          <button type="button" class="btn-error-ok" (click)="clearError()">OK</button>
        </div>
      </div>
      <button type="button" class="close-toast-btn" (click)="clearError()">×</button>
    </div>
  </div>
}